import { useEffect, useState } from "react";
import { Zap, TriangleAlert, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { relTime } from "@/lib/constants";

function useTick(active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, [active]);
  return now;
}

export function EmergentTimer({ startedAt, className = "" }) {
  const now = useTick(!!startedAt);
  if (!startedAt) return null;
  const secs = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const label =
    h > 0
      ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      : `${m}:${String(s).padStart(2, "0")}`;
  return (
    <span className={`tabular-nums ${className}`} data-testid="emergent-timer">
      {label}
    </span>
  );
}

export function EmergentButton({ meActive, activeCount, startedAt, onToggle }) {
  const othersActive = activeCount > (meActive ? 1 : 0);
  return (
    <button
      data-testid="emergent-toggle-button"
      onClick={onToggle}
      title={meActive ? "You're marked as on Emergent — click to clear" : "Let the team know you're using Emergent"}
      className={`group flex items-center gap-2 h-9 px-3 rounded-full border text-sm font-semibold transition-all ${
        meActive
          ? "bg-red-600 border-red-500 text-white ember-glow"
          : othersActive
          ? "bg-amber-950/40 border-amber-800/50 text-amber-300 hover:border-amber-600"
          : "bg-[#1E2029] border-[#2E3242] text-gray-300 hover:border-red-900/50 hover:text-white"
      }`}
    >
      <span
        className={`w-2 h-2 rounded-full ${
          meActive ? "bg-white pulse-glow" : othersActive ? "bg-amber-400 pulse-glow" : "bg-gray-500"
        }`}
      />
      <Zap className={`w-4 h-4 ${meActive ? "text-white" : ""}`} />
      <span className="hidden sm:inline whitespace-nowrap">
        {meActive ? "You're on Emergent" : "On Emergent"}
      </span>
      {meActive && startedAt && (
        <EmergentTimer
          startedAt={startedAt}
          className="hidden sm:inline text-xs font-mono text-white/90 border-l border-white/30 pl-2 ml-0.5"
        />
      )}
    </button>
  );
}

export function EmergentBanner({ active, onStop }) {
  const { user } = useAuth();
  if (!active?.length) return null;

  const others = active.filter((a) => a.user_id !== user?.id);
  const mine = active.find((a) => a.user_id === user?.id);

  // Someone else is on Emergent — warn everyone to hold off
  if (others.length) {
    const names = others.map((o) => o.name).join(", ");
    return (
      <div
        data-testid="emergent-banner"
        className="shrink-0 bg-gradient-to-r from-red-950/80 to-red-900/40 border-b border-red-800/50 px-4 md:px-6 py-2.5 flex items-center gap-3"
      >
        <TriangleAlert className="w-4 h-4 text-red-300 shrink-0" />
        <div className="flex items-center -space-x-2 shrink-0">
          {others.slice(0, 4).map((o) => (
            <img
              key={o.user_id}
              src={o.avatar}
              alt={o.name}
              className="w-6 h-6 rounded-full object-cover border-2 border-red-950"
            />
          ))}
        </div>
        <p className="text-sm text-red-100 min-w-0">
          <span className="font-bold">{names}</span>{" "}
          {others.length > 1 ? "are" : "is"} working on Emergent right now —{" "}
          <span className="font-semibold">please don't use it.</span>
          <span className="hidden sm:inline text-red-300/80">
            {" "}
            On for <EmergentTimer startedAt={others[0].started_at} />.
          </span>
        </p>
        {mine && (
          <button
            data-testid="emergent-banner-stop"
            onClick={onStop}
            className="ml-auto shrink-0 flex items-center gap-1 text-xs text-red-200 hover:text-white bg-red-900/50 hover:bg-red-800/60 border border-red-700/50 rounded-md px-2 py-1"
          >
            <X className="w-3 h-3" /> I'm done
          </button>
        )}
      </div>
    );
  }

  // Only you are on Emergent — gentle reminder to clear it when done
  return (
    <div
      data-testid="emergent-banner-self"
      className="shrink-0 bg-[#1E2029] border-b border-red-900/30 px-4 md:px-6 py-2 flex items-center gap-2.5"
    >
      <span className="w-2 h-2 rounded-full bg-red-500 pulse-glow shrink-0" />
      <p className="text-sm text-gray-300 min-w-0 truncate">
        You're <span className="font-semibold text-red-300">on Emergent</span> for{" "}
        <EmergentTimer startedAt={mine?.started_at} className="text-red-300 font-semibold" /> — the
        team's been asked to hold off.
      </p>
      <button
        data-testid="emergent-banner-stop"
        onClick={onStop}
        className="ml-auto shrink-0 flex items-center gap-1 text-xs text-gray-300 hover:text-white bg-[#262936] hover:bg-[#2E3242] border border-[#2E3242] rounded-md px-2 py-1"
      >
        <X className="w-3 h-3" /> I'm done
      </button>
    </div>
  );
}
